import { NgFor, NgIf } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { LoaderComponent, ModalService, PopperHelper } from "@nts/std";
import { PDFDocumentProxy, PdfViewerComponent, PdfViewerModule } from "ng2-pdf-viewer";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";
import { Subject, debounceTime, takeUntil } from "rxjs";
import { SignerLayout } from "src/app/sign-template-long-op/domain-models/signer-layout";
import { SignerLayoutCollectionItemViewModel } from "src/app/sign-template-long-op/view-models/signer-layout.collection-item-view-model";
import { SignerSettingsCollectionItemViewModel } from "src/app/sign-template-long-op/view-models/signer-settings.collection-item-view-model";
import { SignerSettingsCollectionViewModel } from "src/app/sign-template-long-op/view-models/signer-settings.collection-view-model";

export interface RectInterface {
    /**
     * Posizione left in px
     */
    left: number,

    /**
     * Larghezza in px
     */
    width: number,

    /**
     * Posizione top in px
     */
    top: number,

    /**
     * Altezza in px
     */
    height: number,
}

@UntilDestroy()
@Component({
    selector: 'nts-custom-pdf-editor',
    templateUrl: './custom-pdf-editor.component.html',
    styleUrls: ['./custom-pdf-editor.component.scss'],
    standalone: true,
    imports: [
        LoaderComponent,
        NgFor,
        NgxPopperjsModule,
        PdfViewerModule,
        NgIf
    ]
})
export class CustomPdfEditorComponent {

    /**
     * Gestione errori
     */
    @Input() showErrorTooltip = true;
    @Input() showErrorBorder = true;
    @Input() errorList: string[] = [];
    @Input() currentSignaturePosition: SignerLayoutCollectionItemViewModel = null;

    // /**
    //  * Top iniziale del rettangolo in PDF point
    //  */
    // @Input() topPdfPoint: number = null;

    // /**
    //  * Left iniziale del rettangolo in PDF point
    //  */
    // @Input() leftPdfPoint: number = null;

    /**
    * Altezza personalizzata in mm
    */
    @Input() customPageHeight: number = 0;

    /**
     * Larghezza personalizzata in mm
     */
    @Input() customPageWidth: number = 0;

    /**
     * Placeholder da visualizzare quando non sono state passati i pdf point
     */
    @Input() placeholder: string = ''

    /**
     * Altezza fissa rettangolo in mm
     */
    @Input() rectangleHeight: number = 17;

    /**
     * Larghezza fissa rettangolo in mm
     */
    @Input() rectangleWidth: number = 60;

    @Input() currentPage: number = 1;

    @Input() templateSignersColors: string[] = [
        'red',
        'blue',
        'orange',
        'green',
        'black',
        'grey'
    ]

    @Input() currentFile: File;

    /**
    * Altezza fissa del container del canvas
    */
    @Input() initialWrapperHeight = 1000;
    @Input() initialWrapperWidth = 1000;

    @Input() layouts: SignerSettingsCollectionViewModel = null;

    /**
     * Qaundo si sposta il rettangolo o si resettano i point viene emesso questo evento
     */
    @Output() pdfPointChanged: EventEmitter<{ topPdfPoint: number, leftPdfPoint: number }> = new EventEmitter();

    @Output() currentSignaturePositionChange: EventEmitter<SignerLayoutCollectionItemViewModel> = new EventEmitter();

    @Output() onPdfChanged: EventEmitter<{ proxy: PDFDocumentProxy, file: File }> = new EventEmitter<{ proxy: PDFDocumentProxy, file: File }>()
    @Output() onUpdateRectDimension: EventEmitter<{
        /**
         * Altezza in pixel
         */
        height: number,

        /**
         * Lunghezza in pixel
         */
        width: number
    }> = new EventEmitter();

    @ViewChild('canvasWrapper') canvasWrapper: NgxPopperjsDirective;
    @ViewChild('queryCanvas') queryCanvas: ElementRef;
    @ViewChild('pdfviewer') pdfViewer: PdfViewerComponent;

    ngxPopperjsTriggers = NgxPopperjsTriggers;
    ngxPopperjsPlacements = NgxPopperjsPlacements;
    showLoader = true;

    tallsX = Array(25);
    tallsY = Array(25);

    currentSignaturePositionChanged = new Subject<void>();
    layoutsChanged = new Subject<void>();

    /**
     * Altezza calcolata del rettangolo in mm
     */
    calculatedRectangleWidth: number = this.rectangleWidth;

    /**
     * Larghezza calcolata del rettangolo in mm
     */
    calculatedRectangleHeight: number = this.rectangleHeight;

    /**
     * 1 mm = 2.8346456692912997 PDF Point
     */
    pdfPointConversionRatio = 2.8346456692912997;

    /**
     *  Debouncer per l'evento pdfPointChanged
     */
    pdfPointChangedDebouncer: Subject<{
        leftPdfPoint: number,
        topPdfPoint: number
    }> = new Subject();

    /**
     * Coordinata X del mouse da quando è iniziato il mousedown
     */
    mouseDownStartX = null;

    /**
     * Coordinata Y del mouse da quando è iniziato il mousedown
     */
    mouseDownStartY = null;

    /**
     * Coordinata X del mouse quando è sopra il container del canvas
     */
    mouseX = null;

    /**
     * Coordinata Y del mouse quando è sopra il container del canvas
     */
    mouseY = null;

    /**
     * Verifica se si sta facendo il drag con il mouse
     */
    // isDragging = false;

    // Coordinate di partenza riquadro in mm
    // th_left = this.leftPdfPoint / this.pdfPointConversionRatio;
    // th_top = this.topPdfPoint / this.pdfPointConversionRatio;
    // th_right = this.th_left + this.calculatedRectangleWidth;
    // th_bottom = this.th_top + this.calculatedRectangleHeight;

    // Dimensioni riquadro in mm
    // th_width = this.th_right - this.th_left;
    // th_height = this.th_bottom - this.th_top;

    currentRectanglesPosition: { [key: number]: SignerLayoutCollectionItemViewModel[] } = {};

    // rect: RectInterface = {
    //     left: null,
    //     width: null,
    //     top: null,
    //     height: null,
    // };

    /**
     * Left corrente in mm del rettangolo
     */
    // currentRectangleLeftMM = 0;

    // /**
    // * Top corrente in mm del rettangolo
    // */
    // currentRectangleTopMM = 0;

    // /**
    // * Right corrente in mm del rettangolo
    // */
    // currentRectangleRightMM = 0;

    // /**
    // * Bottom corrente in mm del rettangolo
    // */
    // currentRectangleBottomMM = 0;

    /**
     * Rapporto di conversione per il righello
     */
    rulerConversionRatio = 0;

    currentFileData: Uint8Array = null;
    isLoaded: boolean = false;
    totalPages: number = 0;
    proxy: PDFDocumentProxy;
    currentPageView: number[] = null;

    pageWidthInMM: number;
    pageHeightInMM: number;

    constructor(
        private cd: ChangeDetectorRef,
        private modalService: ModalService
    ) {
        this.pdfPointChangedDebouncer.pipe(untilDestroyed(this), debounceTime(100)).subscribe((value) => {
            this.pdfPointChanged.emit(value)
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['currentFile']?.currentValue) {
            this.currentFile = changes['currentFile']?.currentValue;
            this.loadPdfFromFile(this.currentFile, false);
        }
        // if (changes['errorList']) {
        //     this.checkPopper
        //     return;
        // }

        if (changes['errorList']) {
            this.checkPopper();
            //     return;
        }
        if (this.canvasWrapper && this.isLoaded &&
            (
                changes['currentPage'] ||
                changes['customPageHeight'] ||
                changes['customPageWidth'] ||
                changes['rectangleHeight'] ||
                changes['rectangleWidth']
            )
        ) {
            this.initCanvas();
        }

        if (changes['layouts']) {
            this.layoutsChanged.next();
            this.layouts.collectionChanged.pipe(
                untilDestroyed(this),
                takeUntil(this.layoutsChanged)
            ).subscribe(() => {
                this.initCanvas();
                this.cd.detectChanges();
            })
        }

        if (changes['currentSignaturePosition']) {
            this.currentSignaturePositionChanged.next();
            if (this.currentSignaturePosition) {
                this.currentSignaturePosition.topCoordinateInMM.propertyChanged.pipe(
                    untilDestroyed(this),
                    takeUntil(this.currentSignaturePositionChanged)
                ).subscribe(() => {

                    // Convert pdf points
                    this.currentSignaturePosition.topCoordinate.value = Math.round(this.currentSignaturePosition.topCoordinateInMM.value * this.pdfPointConversionRatio);

                    this.initCanvas();
                    this.cd.detectChanges();
                })
                this.currentSignaturePosition.leftCoordinateInMM.propertyChanged.pipe(
                    untilDestroyed(this),
                    takeUntil(this.currentSignaturePositionChanged)
                ).subscribe(() => {

                    // Convert pdf points
                    this.currentSignaturePosition.leftCoordinate.value = Math.round(this.currentSignaturePosition.leftCoordinateInMM.value * this.pdfPointConversionRatio);

                    this.initCanvas();
                    this.cd.detectChanges();
                })

                this.currentSignaturePosition.lastPage.propertyChanged.pipe(
                    untilDestroyed(this),
                    takeUntil(this.currentSignaturePositionChanged)
                ).subscribe(() => {


                    this.initCanvas();
                    this.cd.detectChanges();
                })

                this.currentSignaturePosition.specificPage.propertyChanged.pipe(
                    untilDestroyed(this),
                    takeUntil(this.currentSignaturePositionChanged)
                ).subscribe(() => {


                    this.initCanvas();
                    this.cd.detectChanges();
                })
            } else {
                this.initCanvas();
            }
        }
    }

    async addSignature(layout: SignerSettingsCollectionItemViewModel, top: number, left: number) {
        const canvas = this.queryCanvas.nativeElement;
        const ratio_w = canvas.width / this.pageWidthInMM;
        const ratio_h = canvas.height / this.pageHeightInMM;

        const th_top = top / ratio_h;
        const th_left = left / ratio_w;

        if (th_left < (this.pageWidthInMM - this.rectangleWidth)) { // non deve sforare la larghezza del foglio

            if (th_top < (this.pageHeightInMM - this.rectangleHeight)) { // non deve sforare la altezza del foglio
                const leftPdfPoint = this.pdfPointConversionRatio * th_left;
                const topPdfPoint = this.pdfPointConversionRatio * th_top;
                const signerLayout: SignerLayout = new SignerLayout();
                signerLayout.topCoordinate = topPdfPoint;
                signerLayout.leftCoordinate = leftPdfPoint;
                signerLayout.specificPage = this.currentPage;
                const vm = await layout.signaturePositions.addFromEntity(signerLayout);
                vm.specificPage.maxValue = this.totalPages;
                this.resetSelectionOfSignatures();
                this.currentSignaturePosition = vm;
                vm.isSelected = true;
                this.currentSignaturePositionChange.next(vm);
                this.initCanvas();
            }

        }
    }

    forcePdfUpdate() {
        (this.pdfViewer as any).update();
        this.showLoader = false;
        this.cd.detectChanges();
    }

    onMouseUp(e: TouchEvent | MouseEvent): void {
        const canvas = this.queryCanvas.nativeElement;
        const draggingSignature = this.getDraggingSignature();
        if (draggingSignature) {
            draggingSignature.isDragging = false;
        }
        canvas.className = '';
    }

    handleRadius = 12;

    drawHandles(signature: SignerLayoutCollectionItemViewModel, fillStyle: string) {
        this.drawCircle(signature.left, signature.top, this.handleRadius, fillStyle);
        this.drawCircle(
            signature.left + signature.width,
            signature.top,
            this.handleRadius,
            fillStyle
        );
        this.drawCircle(
            signature.left + signature.width,
            signature.top + signature.height,
            this.handleRadius,
            fillStyle
        );
        this.drawCircle(
            signature.left,
            signature.top + signature.height,
            this.handleRadius,
            fillStyle
        );
    }

    drawCircle(x: number, y: number, radius: number, fillStyle = '#c757e7') {
        const canvas = this.queryCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = fillStyle;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.closePath();
    }

    /**
     * Aggiorna la posizione e dimensione del rettangolo e lo disegna durante lo spostamento del mouse
     *
     * @param e     mouse event
     */
    onMouseMove(e: TouchEvent | MouseEvent): void {
        const canvas: any = this.queryCanvas.nativeElement;
        const pos = this.getMousePos(canvas, e);
        this.mouseX = pos.x;
        this.mouseY = pos.y;

        let signatureInMousePosition = null
        const draggingSignature = this.getDraggingSignature();
        if (!draggingSignature) {
            signatureInMousePosition = this.getSignatureInMousePosition(this.mouseX, this.mouseY);
        }

        if (draggingSignature) {
            e.preventDefault();
            e.stopPropagation();
            const draggingX = this.mouseX - this.mouseDownStartX;
            const draggingY = this.mouseY - this.mouseDownStartY;
            if (
                draggingSignature.left + draggingX > 0 &&
                draggingSignature.left + draggingX + draggingSignature.width < canvas.width
            ) {
                draggingSignature.left += draggingX;
            }
            if (
                draggingSignature.top + draggingY > 0 &&
                draggingSignature.top + draggingY + draggingSignature.height < canvas.height
            ) {
                draggingSignature.top += draggingY;
            }
            this.mouseDownStartX = this.mouseX;
            this.mouseDownStartY = this.mouseY;
        } else if (signatureInMousePosition) {
            canvas.className = 'drag';
        } else {
            canvas.className = '';
        }
        this.drawRectInCanvas();
    }

    resetSelectionOfSignatures() {
        for (const signerSettings of this.layouts) {

            for (const signature of signerSettings.signaturePositions) {
                signature.isSelected = false;
            }

        }
    }

    /**
     * Trova la prima firma che si trova nella posizione corrente del mouse
     */
    getSignatureInMousePosition(mouseX: number, mouseY: number): SignerLayoutCollectionItemViewModel | null {

        let foundSignature: SignerLayoutCollectionItemViewModel = null;

        for (const key in this.currentRectanglesPosition) {

            for (const signature of this.currentRectanglesPosition[key]) {
                if (this.checkInRect(mouseX, mouseY, signature)) {
                    foundSignature = signature;
                    break;
                }
            }
            if (foundSignature) {
                break;
            }
        }
        return foundSignature;
    }

    /**
     * Trova la prima firma che si trova nella posizione corrente del mouse
     */
    getDraggingSignature() {

        let foundSignature = null;

        for (const key in this.currentRectanglesPosition) {

            for (const signature of this.currentRectanglesPosition[key]) {
                if (signature.isDragging) {
                    foundSignature = signature;
                    break;
                }
            }
            if (foundSignature) {
                break;
            }
        }
        return foundSignature;
    }

    onMouseDown(e: TouchEvent | MouseEvent): void {
        const canvas = this.queryCanvas.nativeElement;
        const pos = this.getMousePos(canvas, e);
        this.mouseX = pos.x;
        this.mouseY = pos.y;

        const signature = this.getSignatureInMousePosition(this.mouseX, this.mouseY);
        if (signature) {
            this.resetSelectionOfSignatures();
            signature.isSelected = true;
            this.currentSignaturePosition = signature;
            this.currentSignaturePositionChange.emit(this.currentSignaturePosition);
            signature.isDragging = true;
            this.mouseDownStartX = this.mouseX;
            this.mouseDownStartY = this.mouseY;
            canvas.className = 'drag';
        }

        this.drawRectInCanvas();
    }

    /**
     * Disegna il rettangolo nel canvas
     */
    private drawRectInCanvas() {
        // if (this.topPdfPoint >= 0 && this.topPdfPoint != null && this.leftPdfPoint >= 0 && this.leftPdfPoint != null) {
        //     const canvas = this.queryCanvas.nativeElement;
        //     const ctx = canvas.getContext('2d');
        //     ctx.clearRect(0, 0, canvas.width, canvas.height);
        //     ctx.beginPath();
        //     ctx.lineWidth = '3';
        //     ctx.fillStyle = 'rgba(199, 87, 231, 0.2)';
        //     ctx.strokeStyle = '#c757e7';
        //     ctx.rect(this.rect.left, this.rect.top, this.rect.width, this.rect.height);
        //     ctx.fill();
        //     ctx.stroke();


        //     ctx.font = "bold 15px Poppins";
        //     ctx.textAlign = "center";
        //     ctx.textBaseline = "middle";
        //     ctx.fillStyle = "#ff0000";
        //     ctx.fillText("Firmatario", this.rect.left + (this.rect.width / 2), this.rect.top + (this.rect.height / 2));
        //     // this.drawHandles();
        //     // this.drawRuler();
        //     this.updateStatistics();
        // }
        const canvas = this.queryCanvas.nativeElement;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (this.checkIfCurrentPageHasSignatures()) {
            for (const key in this.currentRectanglesPosition) {

                for (const signature of this.currentRectanglesPosition[key]) {
                    ctx.beginPath();
                    ctx.rect(signature.left, signature.top, signature.width, signature.height);
                    ctx.lineWidth = '3';
                    ctx.strokeStyle = this.templateSignersColors[key];
                    ctx.fillStyle = 'rgba(199, 87, 231, 0.2)';
                    ctx.textBaseline = "middle";
                    ctx.closePath();
                    ctx.stroke();
                    ctx.fill();

                    ctx.font = "bold 15px Poppins";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";
                    ctx.fillStyle = "#ff0000";
                    ctx.fillText(this.layouts[key].signerLabel.value, signature.left + (signature.width / 2), signature.top + (signature.height / 2));
                    //     // this.drawHandles();

                    if (signature.isSelected) {
                        this.drawHandles(signature, this.templateSignersColors[key])
                    } else if (signature.isDragging) {
                        this.drawHandles(signature, this.templateSignersColors[key])
                    } else if (this.getSignatureInMousePosition(this.mouseX, this.mouseY) === signature) {
                        this.drawHandles(signature, this.templateSignersColors[key])
                    }

                    this.updateStatistics();

                }
            }
        }

    }

    /**
     * Recupera le coordinate x e y del mouse relative al canvas
     * @param canvas    canvas
     * @param evt       evento touch o del mouse
     * @returns         coordinate x e y
     */
    private getMousePos(canvas: any, evt: TouchEvent | MouseEvent): {
        x: number,
        y: number
    } {
        let clientX: number, clientY: number;
        if (evt.type == 'touchstart' || evt.type == 'touchmove') {
            clientX = (evt as TouchEvent).touches[0].clientX;
            clientY = (evt as TouchEvent).touches[0].clientY;
        } else {
            clientX = (evt as MouseEvent).clientX;
            clientY = (evt as MouseEvent).clientY;
        }
        var boundingRect = canvas.getBoundingClientRect();
        return {
            x: clientX - boundingRect.left,
            y: clientY - boundingRect.top,
        };
    }

    /**
    * Verifica che le coordinate passate sia dentro le coordinate del rettangolo passate
    *
    * @param x coordinata x da verificare
    * @param y coordinata y da verificare
    * @param r coordinate rettangolo
    * @returns verso se sono interno altrimenti falso
    */
    private checkInRect(x: number, y: number, r: RectInterface): boolean {
        return (
            x > r.left && x < r.width + r.left && y > r.top && y < r.top + r.height
        );
    }

    /**
     * Aggiorna le varabili per le statistiche
     */
    private updateStatistics() {
        // const canvas = this.queryCanvas.nativeElement;
        // const inverse_ratio_w = this.pageWidthInMM / canvas.width;
        // const inverse_ratio_h = this.pageHeightInMM / canvas.height;
        // this.currentRectangleLeftMM = Math.round(this.rect.left * inverse_ratio_w);
        // this.currentRectangleTopMM = Math.round(this.rect.top * inverse_ratio_h);
        // this.currentRectangleRightMM = Math.round(
        //     (this.rect.left + this.rect.width) * inverse_ratio_w
        // );
        // this.currentRectangleBottomMM = Math.round(
        //     (this.rect.top + this.rect.height) * inverse_ratio_h
        // );

        // this.leftPdfPoint = this.currentRectangleLeftMM * this.pdfPointConversionRatio;
        // this.topPdfPoint = this.currentRectangleTopMM * this.pdfPointConversionRatio;

        // this.pdfPointChangedDebouncer.next({
        //     signature
        //     leftPdfPoint: this.leftPdfPoint,
        //     topPdfPoint: this.topPdfPoint
        // });
        const canvas = this.queryCanvas.nativeElement;
        const inverse_ratio_w = this.pageWidthInMM / canvas.width;
        const inverse_ratio_h = this.pageHeightInMM / canvas.height;

        for (const key in this.currentRectanglesPosition) {

            for (const signature of this.currentRectanglesPosition[key]) {
                signature.topCoordinateInMM.value = Math.round(signature.top * inverse_ratio_h);
                signature.topCoordinate.value = signature.topCoordinateInMM.value * this.pdfPointConversionRatio;

                signature.leftCoordinateInMM.value = Math.round(signature.left * inverse_ratio_w);
                signature.leftCoordinate.value = signature.leftCoordinateInMM.value * this.pdfPointConversionRatio;
            }
        }

    }

    private adaptRectangleSize() {

        // Riadatto il rettangolo
        if (this.rectangleHeight > this.pageHeightInMM) {
            this.calculatedRectangleHeight = this.pageWidthInMM - 2;
        } else {
            this.calculatedRectangleHeight = this.rectangleHeight;
        }
        if (this.rectangleWidth > this.pageWidthInMM) {
            this.calculatedRectangleWidth = this.pageWidthInMM - 2;
        } else {
            this.calculatedRectangleWidth = this.rectangleWidth;
        }
    }

    private updateRuler() {
        const canvas = this.queryCanvas.nativeElement;
        this.rulerConversionRatio = canvas.height / this.pageHeightInMM;
    }

    createRect() {
        // this.topPdfPoint = 0;
        // this.leftPdfPoint = 0;
        // this.initCanvas();
    }

    private checkPopper() {
        if (this.errorList.length === 0) {
            PopperHelper.hide(this.canvasWrapper);
        }
    }

    // private updateRectFromPdfPoints() {
    //     this.th_left = this.leftPdfPoint / this.pdfPointConversionRatio;
    //     this.th_top = this.topPdfPoint / this.pdfPointConversionRatio;
    //     this.th_right = this.th_left + this.calculatedRectangleWidth;
    //     this.th_bottom = this.th_top + this.calculatedRectangleHeight;

    //     // Dimensioni riquadro in mm
    //     this.th_width = this.th_right - this.th_left;
    //     this.th_height = this.th_bottom - this.th_top;
    // }

    private updateRectanglesPositionFromPdfPoints() {

        this.currentRectanglesPosition = {};
        const canvas = this.queryCanvas.nativeElement;


        const ratio_w = canvas.width / this.pageWidthInMM;
        const ratio_h = canvas.height / this.pageHeightInMM;

        for (const [signerNumber, signer] of this.layouts.entries()) {
            const signatures = this.getSignaturesForSpecificPageAndSigner(this.currentPage, signerNumber)
            if (signatures?.length > 0) {

                this.currentRectanglesPosition[signerNumber] = signatures.map((s) => {

                    s.topCoordinateInMM.value = Math.round(s.topCoordinate.value / this.pdfPointConversionRatio);
                    s.leftCoordinateInMM.value = Math.round(s.leftCoordinate.value / this.pdfPointConversionRatio);

                    this.onUpdateRectDimension.emit({
                        height: (s.topCoordinateInMM.value + (this.calculatedRectangleHeight - s.topCoordinateInMM.value)) * ratio_h,
                        width: (s.leftCoordinateInMM.value + (this.calculatedRectangleWidth - s.leftCoordinateInMM.value)) * ratio_w
                    });

                    s.height = (s.topCoordinateInMM.value + (this.calculatedRectangleHeight - s.topCoordinateInMM.value)) * ratio_h;
                    s.width = (s.leftCoordinateInMM.value + (this.calculatedRectangleWidth - s.leftCoordinateInMM.value)) * ratio_w;
                    s.top = s.topCoordinateInMM.value * ratio_h;
                    s.left = s.leftCoordinateInMM.value * ratio_w;
                    s.isSelected = this.currentSignaturePosition === s;

                    return s;
                })
            }
        }
    }

    // private updateRectanglesPositionFromPdfPoints() {

    //     this.currentRectanglesPosition = {};
    //     const canvas = this.queryCanvas.nativeElement;


    //     const ratio_w = canvas.width / this.pageWidthInMM;
    //     const ratio_h = canvas.height / this.pageHeightInMM;

    //     for (const [signerNumber, signer] of this.layouts.entries()) {
    //         const signatures = this.getSignaturesForSpecificPageAndSigner(this.currentPage, signerNumber)
    //         if (signatures?.length > 0) {

    //             this.currentRectanglesPosition[signerNumber] = signatures.map((s) => {

    //                 const topCoordinateInMM = Math.round(s.topCoordinate.value / this.pdfPointConversionRatio);
    //                 const leftCoordinateInMM = Math.round(s.leftCoordinate.value / this.pdfPointConversionRatio);

    //                 this.onUpdateRectDimension.emit({
    //                     height: (topCoordinateInMM + (this.calculatedRectangleHeight - topCoordinateInMM)) * ratio_h,
    //                     width: (leftCoordinateInMM + (this.calculatedRectangleWidth - leftCoordinateInMM)) * ratio_w
    //                 });

    //                 s.height = (topCoordinateInMM + (this.calculatedRectangleHeight - topCoordinateInMM)) * ratio_h;
    //                 s.width = (leftCoordinateInMM + (this.calculatedRectangleWidth - leftCoordinateInMM)) * ratio_w;
    //                 s.top = topCoordinateInMM * ratio_h;
    //                 s.left = leftCoordinateInMM * ratio_w;
    //                 s.isSelected = this.currentSignaturePosition === s

    //                 return s;
    //             })
    //         }
    //     }
    // }

    private initCanvas() {
        if (!this.isLoaded) {
            return
        }
        this.checkPopper();
        // this.checkDimension();

        const canvas = this.queryCanvas.nativeElement;

        canvas.height = this.initialWrapperHeight
            - 2; // canvas border
        canvas.width = canvas.height * this.pageWidthInMM / this.pageHeightInMM;

        this.updateRuler();


        if (this.checkIfCurrentPageHasSignatures()) {
            this.adaptRectangleSize();
            this.updateRectanglesPositionFromPdfPoints();
            this.drawRectInCanvas();
        } else {
            const canvas = this.queryCanvas.nativeElement;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            this.currentRectanglesPosition = {};
        }


        // if (this.topPdfPoint >= 0 && this.topPdfPoint != null && this.leftPdfPoint >= 0 && this.leftPdfPoint != null) {

        // } else {
        // this.pdfPointChangedDebouncer.next({
        //     leftPdfPoint: null,
        //     topPdfPoint: null
        // });
        // }

        // const hrulerCanvas = document.getElementById('hruler-canvas') as any;
        // hrulerCanvas.width = canvasWrapper.offsetWidth;
        // hrulerCanvas.height = 20;

        // const vrulerCanvas = document.getElementById('vruler-canvas') as any;
        // vrulerCanvas.width = 20;
        // vrulerCanvas.height = canvasWrapper.offsetHeight;

        // this.effective_image_width = canvas.width;
        // this.effective_image_height = canvas.height;
        // canvas.style.top = canvasWrapper.offsetTop + 'px';
        // canvas.style.left = canvasWrapper.offsetLeft + 'px';
        // this.updateCurrentCanvasRect();
    }

    getSignaturesForSpecificPageAndSigner(page: number, signerNumber: number): SignerLayoutCollectionItemViewModel[] {
        const pageIsLastPage = page === this.totalPages;
        const signatures: SignerLayoutCollectionItemViewModel[] = [];

        for (const signaturePosition of this.layouts[signerNumber].signaturePositions) {
            if (signaturePosition.specificPage.value === page || (pageIsLastPage && signaturePosition.lastPage.value)) {
                signatures.push(signaturePosition);
            }
        }
        return signatures;
    }

    checkIfCurrentPageHasSignatures() {
        for (const [signerNumber, signerSettingsVm] of this.layouts.entries()) {
            const signatures = this.getSignaturesForSpecificPageAndSigner(this.currentPage, signerNumber);
            if (signatures?.length > 0) {
                return true;
            }
        }
        return false;
    }

    async loadPageData(pageNumber: number) {
        const page = await this.proxy.getPage(pageNumber);
        this.currentPageView = page.view;

        const ratio = this.currentPageView[3] / this.currentPageView[2];

        this.pageWidthInMM = page.view[2] / this.pdfPointConversionRatio;
        this.pageHeightInMM = page.view[3] / this.pdfPointConversionRatio;

        this.initialWrapperWidth = Math.floor(this.initialWrapperHeight / ratio);

        this.initCanvas();

        setTimeout(() => this.forcePdfUpdate());

        this.cd.detectChanges();
    }

    async loadPdfFromFile(file: File, resetCurrentPage = true): Promise<void> {
        this.isLoaded = false;
        this.cd.detectChanges();
        if (file) {
            return file.arrayBuffer().then(buff => {
                if (resetCurrentPage) {
                    this.currentPage = 1;
                }
                this.currentFileData = new Uint8Array(buff);
                this.cd.detectChanges();
            });
        }
    }

    async afterLoadComplete(proxy: PDFDocumentProxy) {
        this.proxy = proxy;
        this.totalPages = proxy.numPages;
        await this.loadPageData(this.currentPage);
        this.isLoaded = true;
        this.onPdfChanged.emit({ proxy, file: this.currentFile });
        this.initCanvas();
        this.cd.detectChanges();
    }
}
